import { useCallback, useContext, useMemo } from 'react';
import { GroupSettingsContext } from '../contexts/GroupSettingsContext/GroupSettingsProvider';
import type { EmailFolder } from '../types/common';
import { type FolderData, FOLDER_CATEGORIES } from '../types/folder-label';

interface UseFolderLabelReturn {
    folders: FolderData[];
    addFolder: (name: string) => void;
    deleteFolder: (id: string) => void;
    addSubfolder: (parentId: string, name: string) => void;
    renameFolder: (id: string, newName: string) => void;
    moveFolder: (folderId: string, newParentId: string | null) => void;
}

const convertEmailFolderToFolderData = (folder: EmailFolder): FolderData => ({
    id: folder.id,
    name: folder.name,
    children: folder.children?.map(convertEmailFolderToFolderData) ?? [],
});

const convertFolderDataToEmailFolder = (folder: FolderData): EmailFolder => ({
    id: folder.id,
    name: folder.name,
    children: folder.children.length > 0
        ? folder.children.map(convertFolderDataToEmailFolder)
        : undefined,
});

const ensureRootFolders = (folderList: FolderData[]): FolderData[] => {
    const result: FolderData[] = [];
    for (let i = 0; i < FOLDER_CATEGORIES.length; i++) {
        if (folderList[i]) {
            result.push(folderList[i]);
        } else {
            result.push({
                id: crypto.randomUUID(),
                name: FOLDER_CATEGORIES[i].rootFolderName,
                children: [],
            });
        }
    }
    if (folderList.length > FOLDER_CATEGORIES.length) {
        result.push(...folderList.slice(FOLDER_CATEGORIES.length));
    }
    return result;
};

export const useFolderLabel = (): UseFolderLabelReturn => {
    const { emailFolders, setEmailFolders } = useContext(GroupSettingsContext);

    const folders = useMemo<FolderData[]>(
        () => emailFolders.map(convertEmailFolderToFolderData),
        [emailFolders]
    );

    // Ensure one root folder exists per category
    const ensuredFolders = useMemo<FolderData[]>(
        () => ensureRootFolders(folders),
        [folders]
    );

    const updateFolders = useCallback((updater: (prev: FolderData[]) => FolderData[]) => {
        setEmailFolders((prev) => {
            const currentFolders = ensureRootFolders(prev.map(convertEmailFolderToFolderData));
            const updatedFolders = ensureRootFolders(updater(currentFolders));
            return updatedFolders.map(convertFolderDataToEmailFolder);
        });
    }, [setEmailFolders]);

    const addFolder = useCallback((name: string) => {
        const newFolder: FolderData = {
            id: crypto.randomUUID(),
            name,
            children: [],
        };
        updateFolders((prev) => [...prev, newFolder]);
    }, [updateFolders]);

    const deleteFolder = useCallback((id: string) => {
        const removeFolder = (folderList: FolderData[]): FolderData[] => {
            return folderList
                .filter((folder) => folder.id !== id)
                .map((folder) => ({
                    ...folder,
                    children: removeFolder(folder.children),
                }));
        };
        updateFolders(removeFolder);
    }, [updateFolders]);

    const addSubfolder = useCallback((parentId: string, subfolderName: string) => {
        const newFolder: FolderData = {
            id: crypto.randomUUID(),
            name: subfolderName,
            children: [],
        };

        const addToParent = (folderList: FolderData[]): FolderData[] => {
            return folderList.map((folder) => {
                if (folder.id === parentId) {
                    return { ...folder, children: [...folder.children, newFolder] };
                }
                if (folder.children.length > 0) {
                    return { ...folder, children: addToParent(folder.children) };
                }
                return folder;
            });
        };
        updateFolders(addToParent);
    }, [updateFolders]);

    const renameFolder = useCallback((id: string, newName: string) => {
        const rename = (folderList: FolderData[]): FolderData[] => {
            return folderList.map((folder) => {
                if (folder.id === id) {
                    return { ...folder, name: newName };
                }
                if (folder.children.length > 0) {
                    return { ...folder, children: rename(folder.children) };
                }
                return folder;
            });
        };
        updateFolders(rename);
    }, [updateFolders]);

    const moveFolder = useCallback((folderId: string, newParentId: string | null) => {
        updateFolders((prev) => {
            let folderToMove: FolderData | null = null;

            // Find and remove the folder
            const findAndRemove = (folderList: FolderData[]): FolderData[] => {
                return folderList
                    .filter((folder) => {
                        if (folder.id === folderId) {
                            folderToMove = folder;
                            return false;
                        }
                        return true;
                    })
                    .map((folder) => ({
                        ...folder,
                        children: findAndRemove(folder.children),
                    }));
            };

            const withoutFolder = findAndRemove(prev);

            if (!folderToMove) return prev;

            // Add to new parent or root
            if (newParentId === null) {
                return [...withoutFolder, folderToMove];
            }

            const addToNewParent = (folderList: FolderData[]): FolderData[] => {
                return folderList.map((folder) => {
                    if (folder.id === newParentId) {
                        return { ...folder, children: [...folder.children, folderToMove!] };
                    }
                    if (folder.children.length > 0) {
                        return { ...folder, children: addToNewParent(folder.children) };
                    }
                    return folder;
                });
            };

            return addToNewParent(withoutFolder);
        });
    }, [updateFolders]);

    return {
        folders: ensuredFolders,
        addFolder,
        deleteFolder,
        addSubfolder,
        renameFolder,
        moveFolder,
    };
};
